import React, { useCallback, useEffect, useState } from 'react';
import { fetchTerminalsList, deleteTerminal, fetchPortsList } from './services/api';
import { Terminal, Port } from './types';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
// import 'react-loading-skeleton/dist/skeleton.css';
import { Button, Form } from 'react-bootstrap';
import { useAuth } from './AuthContext';

const TerminalList: React.FC = () => {
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [ports, setPorts] = useState<Port[]>([]);
  const [selectedPort, setSelectedPort] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { token } = useAuth();

  const fetchTerminals = useCallback(async (portId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchTerminalsList();
      const terminals = Array.isArray(response.data) ? response.data : [];

      // Find the selected port to get its name
      const selectedPortObj = ports.find(p => String(p.id) === portId);

      // Filter terminals and add port name if missing
      const filteredTerminals = terminals
        .filter(t => String(t.port) === portId)
        .map(t => ({
          ...t,
          port_name: t.port_name || selectedPortObj?.name || 'Unknown Location'
        }));

      setTerminals(filteredTerminals);
    } catch (error) {
      console.error('Error fetching terminals:', error);
      setError('Failed to load terminals');
    } finally {
      setLoading(false);
    }
  }, [ports]);

  useEffect(() => {
    fetchPortsList()
      .then(response => {
        const ports = Array.isArray(response.data) ? response.data : [];
        setPorts(ports);
      })
      .catch(() => setPorts([]));

    // if (token && token.port) {
    //   setSelectedPort(String(token.port));
    //   fetchTerminals(String(token.port));
    // }
  }, []);

  const handlePortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const portId = e.target.value;
    setSelectedPort(portId);
    if (portId) fetchTerminals(portId);
    else setTerminals([]);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this terminal?')) return;
    setDeletingId(id);
    try {
      await deleteTerminal(id);
      setTerminals(ts => ts.filter(t => t.id !== id));
    } catch {
      alert('Failed to delete terminal');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-4 p-md-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Geo Fence Location</h2>
        <Button variant="primary" onClick={() => navigate('/terminals/create')}>
          <i className="fas fa-plus me-2"></i> Create Terminal
        </Button>
      </div>
      <Form.Group className="mb-2">
        <Form.Label>Filter by Locations</Form.Label>
        <Form.Select value={selectedPort} onChange={handlePortChange} disabled={!!(token && token.designation?.user_type === 'ADMIN' && token.port)}>
          <option value="">Select Location</option>
          {ports.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </Form.Select>
      </Form.Group>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Location</th>
              <th>Latitude</th>
              <th>Longitude</th>
              <th>Geofence Area (m)</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td><Skeleton /></td>
                  <td><Skeleton /></td>
                  <td><Skeleton width={80} /></td>
                  <td><Skeleton width={80} /></td>
                  <td><Skeleton width={60} /></td>
                  <td><Skeleton width={70} /></td>
                </tr>
              ))
            ) : terminals.length === 0 ? (
              <tr><td colSpan={6} className="text-center">{selectedPort ? 'No terminals found for this port.' : 'Select a port to view terminals.'}</td></tr>
            ) : (
              terminals.map(t => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td>{t.port_name}</td>
                  <td>{t.latitude}</td>
                  <td>{t.longitude}</td>
                  <td>{t.geofence_area}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => navigate(`/terminals/edit/${t.id}`)}>
                      <i className="fas fa-edit"></i>
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(t.id)} disabled={deletingId === t.id}>
                      {deletingId === t.id ? <span className="spinner-border spinner-border-sm" /> : <i className="fas fa-trash"></i>}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TerminalList; 